const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "h1",
  "h2",
  "h3",
  "ul",
  "ol",
  "li",
  "blockquote",
  "a",
]);

const RAW_TEXT_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "noscript",
  "noembed",
  "template",
  "textarea",
  "title",
  "xmp",
  "svg",
  "math",
  "frameset",
  "frame",
  "applet",
]);

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: "\u00a0",
};

type ParsedAttr = { name: string; value: string };

type ParsedTag = {
  name: string;
  closing: boolean;
  selfClosing: boolean;
  attrs: ParsedAttr[];
  end: number;
};

function decodeEntitiesOnce(input: string): string {
  return input.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, body: string) => {
    if (body[0] === "#") {
      const hex = body[1] === "x" || body[1] === "X";
      const code = hex ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
      if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return entity;
      try {
        return String.fromCodePoint(code);
      } catch {
        return entity;
      }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? entity : named;
  });
}

function decodeEntities(input: string, rounds = 1): string {
  let current = input;
  for (let i = 0; i < rounds; i++) {
    const next = decodeEntitiesOnce(current);
    if (next === current) break;
    current = next;
  }
  return current;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Remove U+0000. Equivale a substituir esse code unit antes de decodificar entidades.
 * O literal no regex dispara no-control-regex; o laço mantém o mesmo corte.
 */
function stripNullBytes(value: string): string {
  let out = "";
  for (let index = 0; index < value.length; index++) {
    if (value.charCodeAt(index) === 0) continue;
    out += value[index];
  }
  return out;
}

/**
 * Remove C0 (U+0000–U+001F), DEL (U+007F) e a barra invertida.
 * Equivale à classe [\u0000-\u001F\u007F\\] sem alargar o que o href aceita.
 */
function stripHrefControls(value: string): string {
  let out = "";
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f || value[index] === "\\") continue;
    out += value[index];
  }
  return out;
}

function canonicalTag(name: string): string | null {
  const normalized =
    name === "b" ? "strong" : name === "i" ? "em" : name === "strike" || name === "del" ? "s" : name;
  return ALLOWED_TAGS.has(normalized) ? normalized : null;
}

function indexOfTagEnd(src: string, from: number): number {
  let quote = "";
  for (let i = from; i < src.length; i++) {
    const char = src[i];
    if (quote) {
      if (char === quote) quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === ">") return i;
  }
  return -1;
}

function parseTag(src: string, start: number): ParsedTag | null {
  if (src[start] !== "<") return null;

  let i = start + 1;
  let closing = false;
  if (src[i] === "/") {
    closing = true;
    i++;
  }

  if (!/[A-Za-z]/.test(src[i] || "")) return null;

  const nameStart = i;
  i++;
  while (i < src.length && /[A-Za-z0-9]/.test(src[i])) i++;
  const name = src.slice(nameStart, i);

  if (src[i] === ":" || src[i] === "/" && src[i + 1] !== ">" && !/\s/.test(src[i + 1] || "")) {
    const end = indexOfTagEnd(src, i);
    if (end === -1) return null;
    return { name: "", closing, selfClosing: false, attrs: [], end: end + 1 };
  }

  const attrs: ParsedAttr[] = [];
  let selfClosing = false;

  while (i < src.length) {
    while (i < src.length && /\s/.test(src[i])) i++;
    if (i >= src.length) return null;

    if (src[i] === ">") {
      i++;
      break;
    }

    if (src[i] === "/") {
      i++;
      while (i < src.length && /\s/.test(src[i])) i++;
      if (src[i] !== ">") {
        const end = indexOfTagEnd(src, i);
        if (end === -1) return null;
        return { name: "", closing, selfClosing: false, attrs: [], end: end + 1 };
      }
      selfClosing = true;
      i++;
      break;
    }

    if (!/[A-Za-z]/.test(src[i])) {
      const end = indexOfTagEnd(src, i);
      if (end === -1) return null;
      return { name: "", closing, selfClosing: false, attrs: [], end: end + 1 };
    }

    const attrStart = i;
    i++;
    while (i < src.length && /[A-Za-z0-9_:-]/.test(src[i])) i++;
    const attrName = src.slice(attrStart, i).toLowerCase();
    while (i < src.length && /\s/.test(src[i])) i++;

    let value = "";
    if (src[i] === "=") {
      i++;
      while (i < src.length && /\s/.test(src[i])) i++;
      if (src[i] === '"' || src[i] === "'") {
        const quote = src[i];
        i++;
        const valueStart = i;
        while (i < src.length && src[i] !== quote) i++;
        value = src.slice(valueStart, i);
        if (src[i] === quote) i++;
      } else {
        const valueStart = i;
        while (i < src.length && !/[\s>]/.test(src[i])) i++;
        value = src.slice(valueStart, i);
      }
    }

    attrs.push({ name: attrName, value });
  }

  return { name, closing, selfClosing, attrs, end: i };
}

function skipRawElement(src: string, from: number, tagName: string): number {
  const lower = src.toLowerCase();
  const needle = `</${tagName}`;
  let index = lower.indexOf(needle, from);
  while (index !== -1) {
    let cursor = index + needle.length;
    while (cursor < src.length && /\s/.test(src[cursor])) cursor++;
    if (src[cursor] === ">") return cursor + 1;
    index = lower.indexOf(needle, index + 1);
  }
  return src.length;
}

function safeHref(attrs: ParsedAttr[]): string | null {
  const hrefs = attrs.filter((attr) => attr.name === "href");
  if (hrefs.length !== 1) return null;

  const decoded = stripHrefControls(decodeEntities(hrefs[0].value, 5)).trim();

  if (!decoded || /[\s<>"']/.test(decoded)) return null;

  const lower = decoded.toLowerCase();
  if (lower.startsWith("https://") || lower.startsWith("http://")) {
    try {
      const url = new URL(decoded);
      if (url.protocol !== "http:" && url.protocol !== "https:") return null;
      return decoded;
    } catch {
      return null;
    }
  }

  if (lower.startsWith("mailto:")) {
    const address = decoded.slice("mailto:".length).split("?")[0];
    if (!/^[^\s<>"']+@[^\s<>"']+$/.test(address)) return null;
    return decoded;
  }

  return null;
}

function appendText(out: string, raw: string): string {
  const decoded = decodeEntities(stripNullBytes(raw), 1);
  return out + escapeHtml(decoded);
}

export function sanitizeLetterHtml(html: string): string {
  if (!html) return "";

  const src = String(html);
  let cursor = 0;
  let out = "";
  const stack: string[] = [];

  const closeFrom = (index: number) => {
    for (let position = stack.length - 1; position >= index; position--) {
      const open = stack[position];
      if (!open.startsWith("#")) out += `</${open}>`;
    }
    stack.length = index;
  };

  while (cursor < src.length) {
    if (src[cursor] !== "<") {
      const next = src.indexOf("<", cursor);
      const end = next === -1 ? src.length : next;
      out = appendText(out, src.slice(cursor, end));
      cursor = end;
      continue;
    }

    if (src.startsWith("<!--", cursor)) {
      const end = src.indexOf("-->", cursor + 4);
      cursor = end === -1 ? src.length : end + 3;
      continue;
    }

    if (src.startsWith("<!", cursor) || src.startsWith("<?", cursor)) {
      const end = indexOfTagEnd(src, cursor + 2);
      cursor = end === -1 ? src.length : end + 1;
      continue;
    }

    const tag = parseTag(src, cursor);
    if (!tag) {
      out += "&lt;";
      cursor++;
      continue;
    }

    cursor = tag.end;
    if (!tag.name) continue;

    const rawName = tag.name.toLowerCase();
    if (RAW_TEXT_TAGS.has(rawName)) {
      if (!tag.closing && !tag.selfClosing) {
        cursor = skipRawElement(src, cursor, rawName);
      }
      continue;
    }

    const canonical = canonicalTag(rawName);
    if (!canonical) continue;

    if (tag.closing) {
      const marker = canonical === "a" ? "#a" : canonical;
      let index = -1;
      for (let position = stack.length - 1; position >= 0; position--) {
        if (stack[position] === canonical || stack[position] === marker) {
          index = position;
          break;
        }
      }
      if (index !== -1) closeFrom(index);
      continue;
    }

    if (canonical === "br") {
      out += "<br>";
      continue;
    }

    if (canonical === "a") {
      const href = safeHref(tag.attrs);
      if (!href) {
        stack.push("#a");
      } else {
        out += `<a href="${escapeHtml(href)}">`;
        stack.push("a");
      }
      if (tag.selfClosing) closeFrom(stack.length - 1);
      continue;
    }

    out += `<${canonical}>`;
    stack.push(canonical);
    if (tag.selfClosing) closeFrom(stack.length - 1);
  }

  closeFrom(0);
  return out;
}

export function renderHtmlContent(html: string): string {
  return sanitizeLetterHtml(html);
}

export function stripHtmlTags(html: string): string {
  if (!html) return "";
  const sanitized = sanitizeLetterHtml(html);
  const withoutTags = sanitized.replace(/<[^>]*>/g, "");
  return decodeEntities(withoutTags, 1).replace(/\u00a0/g, " ");
}
